import { InputAttributes, NumberFormatValues, NumericFormat, NumericFormatProps, PatternFormat } from "react-number-format"
import { FormikInput, FormikInputProps, Input } from "./input"
import { useFormikContext } from "formik";
import { InputDatePicker } from "./datepicker";
import { Label } from "./label";
import { Col } from "../grid/col";
import { hasErrors } from "../utils/formik.util";
import { FIELD_ERROR_CLASS, FieldError, FieldErrorMessage, useFieldError } from "./field-error";
import { cn } from "@/utils/cn";

interface FormikInputCepProps extends FormikInputProps {
    onCepChange?(value: string): void;
}

interface FormikInputCpfProps extends FormikInputProps {
    onValueChange?(value: string): void;
}

export const FormikInputCpf = (props: FormikInputCpfProps) => {
    const {handleChange, values, touched} = useFormikContext<any>();
    const name = props.field?.name || props.name;
  
    return (
        <PatternFormat 
            {...props}
            type="text"
            defaultValue={""}
            value={props.value || values[name]} 
            format={'###.###.###-##'}
            customInput={FormikInput} 
            allowEmptyFormatting
            onValueChange={(values: NumberFormatValues) => {
                handleChange!(values.value);

                if (props.onValueChange && values.value.length === 11){
                    props.onValueChange(values.value);
                }
            }}
        />
    )
}

export const FormikInputCep = (props: FormikInputCepProps) => {
    const {handleChange, values, touched} = useFormikContext<any>();
    const name = props.field?.name || props.name;
  
    return (
        <PatternFormat 
            {...props}
            type="text"
            defaultValue={""}
            value={props.value || values[name]} 
            format={'##.###-###'}
            customInput={FormikInput} 
            // allowEmptyFormatting
            onValueChange={(values: NumberFormatValues) => {
                handleChange!(values.value);

                if (values.value.length === 8){
                    props.onCepChange!(values.value);
                }
            }}
        />
    )
}

export const FormikInputTelefone = (props: FormikInputProps) => {
    const {handleChange, values} = useFormikContext<any>();
    const name = props.field?.name || props.name;
    const value = props.value || values[name];

    const maskTelefone = "(##) ####-#####";
    const maskCelular = "(##) #####-####";

    const getMask = () => {
        if (!value || value.length < 14){
            return maskTelefone;
        }
        return maskCelular;
    }
    
    return (
        <PatternFormat 
            {...props}
            type="text"
            defaultValue={""}
            value={value} 
            format={getMask()}
            customInput={FormikInput} 
            allowEmptyFormatting
            onValueChange={(values: NumberFormatValues) => {
                handleChange!(values.value)
            }}
        />
    )
}

export const FormikInputDate = (props: FormikInputProps) => {
    const formikProps = useFormikContext<any>();
    const {values, setFieldValue} = formikProps;
    const name = props.field?.name || props.name;
    const value = props.value || values[name];
    const error: boolean = hasErrors(name, formikProps);
                
    var component = (
        <>
            {props.label && (
                <Label>{props.label}</Label>
            )}
            <InputDatePicker error={error}
            onChange={(value: Date) => {
                setFieldValue(name, value.toISOString().split('T')[0])
            }} date={value && new Date(`${value}T03:00:00`)} />
            <FieldError name={name} />
        </>
    )

    if (props.size){
        return <Col xs={12} sm={props.size}>{component}</Col>
    }

    return component;
}

interface FormikInputNumberProps extends Omit<FormikInputProps, 'value'|'defaultValue'>, NumericFormatProps<InputAttributes> {
    suffixLabel?: string;
    prefixLabel?: string;
    value?: number;
    type?: 'text' | 'tel' | 'password';
}


export const FormikInputNumber = ({type='text',...props}: FormikInputNumberProps) => {
    const {values, setFieldValue, setFieldTouched} = useFormikContext<any>();
    const name = props.field?.name || props.name;
    const errorMessage = useFieldError(name);
    const value = props.value || values[name];
    
    var component = (
        <>
            {props.label && (
                <Label>{props.label}</Label>
            )}
            <NumericFormat 
                {...props}
                prefix={props.prefixLabel}
                suffix={props.suffixLabel}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={props.decimalScale}
                fixedDecimalScale
                customInput={Input} 
                name={name}
                aria-invalid={!!errorMessage}
                className={cn(errorMessage && FIELD_ERROR_CLASS, props.className)}
                onBlur={() => setFieldTouched(name, true)}
                onValueChange={(values: NumberFormatValues) => setFieldValue(name, values.value)}
                value={value}
            />
            <FieldErrorMessage message={errorMessage} />
        </>
    )

    if (props.size){
        return <Col xs={12} sm={props.size}>{component}</Col>
    }

    return component;
}